import React, { useEffect, useState } from 'react';
import { CpgJobsLogo } from './CpgJobsLogo';
import { CPGJOBS_SITE_URL } from './cpgSites';
import { QuestionsModal } from '../QuestionsModal';
import './site-header.css';

// Sticky CPGJobs brand bar. Ported from
// Core/NextJS/apps/cpgjobs/app/components/Header/SiteHeader.tsx to CRA. The
// marketing / job-board pages live on the cpgjobs Next.js app, so nav links point
// to its ABSOLUTE URLs via the env-aware CPGJOBS_SITE_URL (prod www.cpgjobs.com,
// dev localhost:3106 — see cpgSites.ts); the logo links out there behind a
// leave-confirmation (see CpgJobsLogo). The Find-a-Job / Post-a-Job CTA chips
// are intentionally OMITTED on this app: these pages are the purchase
// transaction, and we keep people in it. The hamburger drawer state +
// Escape-close + aria wiring are kept verbatim.

const NAV_LINKS = [
  { href: `${CPGJOBS_SITE_URL}/why-post-here`, label: 'Why post here' },
  { href: `${CPGJOBS_SITE_URL}/jobs`, label: 'Browse Jobs' },
  { href: `${CPGJOBS_SITE_URL}/pricing`, label: 'Pricing' },
  { href: `${CPGJOBS_SITE_URL}/faq`, label: 'FAQ' },
];

// Dark-navy pill with a blue circular "?" badge — the in-app help trigger.
// It's an ACTION (opens a dialog), so it renders a <button>, not an <a>.
function QuestionsButton({
  onClick,
  expanded,
  className,
}: {
  onClick: () => void;
  expanded: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={`questions-pill${className ? ` ${className}` : ''}`}
      onClick={onClick}
      aria-haspopup="dialog"
      aria-expanded={expanded}
    >
      <span
        className="questions-badge"
        aria-hidden="true"
      >
        ?
      </span>
      Questions?
    </button>
  );
}

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // Escape closes the mobile drawer (event listener, not a render side effect).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const close = () => setOpen(false);

  const openHelp = () => {
    setOpen(false);
    setHelpOpen(true);
  };

  return (
    <header className="topbar">
      <div className="topbar-inner">
        <CpgJobsLogo />
        <nav
          className="nav"
          aria-label="Primary"
          style={{ marginLeft: 'auto' }}
        >
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <QuestionsButton
          onClick={openHelp}
          expanded={helpOpen}
          className="questions-pill-desktop"
        />
        <button
          type="button"
          className="nav-toggle"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          aria-controls="mobile-nav"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? (
            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          ) : (
            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M4 7h16M4 12h16M4 17h16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          )}
        </button>
      </div>

      <nav
        id="mobile-nav"
        className={`mobile-nav${open ? ' open' : ''}`}
        aria-label="Mobile"
      >
        {NAV_LINKS.map((l) => (
          <a
            key={l.href}
            href={l.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={close}
          >
            {l.label}
          </a>
        ))}
        <QuestionsButton
          onClick={openHelp}
          expanded={helpOpen}
          className="questions-pill-mobile"
        />
      </nav>

      <QuestionsModal
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
      />
    </header>
  );
}

export default SiteHeader;
